import { defaultTo, equals, map, pathOr, pathSatisfies, pipe, replace, split, trim } from 'ramda';
import {
  DEFAULT_CORE_API_AUTH,
  FALSE,
  GRAPHQL_ENDPOINT,
  TRUE,
  canUseLocalDevelopmentConfig,
  isDevelopment,
} from './constants';

/**
 * fetchSiteSettings is used to fetch local site setting file json
 */
export const fetchSiteSettings = async (namespace: string) => {
  if (!canUseLocalDevelopmentConfig) {
    return (window as any).LendsmartBootstrap;
  }
  const namespaceSiteSettingPath = `/data/customization/${namespace}/config/STS_siteSettings.json`;
  return new Promise(async (resolve, reject) => {
    try {
      console.log(
        `LOADING site settings from local path: ${namespaceSiteSettingPath} `,
      );
      const configJson = await fetch(namespaceSiteSettingPath)
        .then((res) => {
          return res.text();
        })
        .then((text) => {
          const jsonData = JSON.parse(text);
          console.log(
            `LOCAL site settings file ${namespaceSiteSettingPath} loaded successfully`,
          );

          return jsonData;
        })
        .catch((err) => {
          reject({});
          console.log(
            'ERROR: failed to fetch or parse requested namespace path',
            namespaceSiteSettingPath,
            err,
          );
        });
      console.log(`LOADED file from local path: ${namespaceSiteSettingPath} `);
      resolve(configJson);
    } catch (err) {
      console.log(
        'ERROR: failed to fetch requested namespace path',
        namespaceSiteSettingPath,
        err,
      );
      reject({});
    }
  });
};

const convertSiteSettingsFlags = (siteSetting: any) => {
  //common
  const namespaceId = pathOr('', ['namespace_id'], siteSetting);
  const entityID = pathOr('', ['entity_id'], siteSetting);

  const uid = pathOr('', ['id'], siteSetting);

  // theme
  const theme = pathOr({}, ['theme'], siteSetting);
  const mainColor = pathOr('', ['theme_color'], theme);
  const extraLightColor = pathOr('', ['extra_light_color'], theme);
  const primaryDarkColor = pathOr('', ['primary_dark_color'], theme);
  const textColor = pathOr('', ['text_color'], theme);
  const buttonColor = pathOr('', ['button', 'primary_color'], theme);
  const buttonBorderColor = pathOr('', ['button', 'border_color'], theme);
  const buttonBorderActiveColor = pathOr('', ['button', 'hover_color'], theme);
  const secondaryColor = pathOr('', ['button', 'secondary_color'], theme);

  const themeWebsiteLogo = pathOr('', ['logos', 'website_logo'], theme);
  const themeWebsiteLogoHeight = pathOr(
    '',
    ['logos', 'website_logo_height'],
    theme,
  );
  const themeWebsiteLogoWidth = pathOr(
    '', ['logos', 'website_logo_width'],
    theme
  )
  const themeWebsiteWelcomePageLogo = pathOr(
    '',
    ['logos', 'website_welcome_page_logo'],
    theme,
  );

  //capabilities
  const capabilities = pathOr({}, ['capabilities'], siteSetting);
  /*** common: related flags */
  const graphqlAppEndpoint =
    isDevelopment && GRAPHQL_ENDPOINT
      ? GRAPHQL_ENDPOINT
      : pathOr('', ['graphql_app_endpoint'], capabilities);
  const graphqlServEndpoint = pathOr(
    '',
    ['graphql_serv_endpoint'],
    capabilities,
  );

  /*** 1. general: related flags */
  const generalAppTitle = pathOr('', ['general_app_title'], capabilities);
  const generalAppTitleWithoutPostFix = trim(
    replace('Bank', '', generalAppTitle),
  );
  const generalHomeRoot = pathOr('', ['general_home_root'], capabilities);
  const generalDefaultRoot = pathOr('', ['general_default_root'], capabilities);
  const generalHeroPageEnabled = pathOr(
    FALSE,
    ['general_hero_page_enabled'],
    capabilities,
  );
  const whoCanAccessInternalGlobalSettingsEmails = map(
    trim,
    split(
      ',',
      pathOr('', ['whocan_access_interal_settings_emails'], capabilities),
    ),
  );

  const generalSmartlookEnabled = equals('true', pathOr(
    'false',
    ['general_smartlook_enabled'],
    capabilities,
  ));

  const generalCookiePolicyConsent = equals('true', pathOr('',
    ['general_web_session_consents_enabled'],
    capabilities
  ));

  const generalSiteCookiePolicyLink = pathOr("https://getattune.com/privacy-policy",
    ['general_site_cookie_policy_link'],
    capabilities
  )

  const generalSmartlookTraceEnabled = pathOr(
    '',
    ['general_smartlook_trace_enabled'],
    capabilities,
  );
  const generalSignupWithTitleEnabled = pathOr(
    true,
    ['general_signup_with_title_enabled'],
    capabilities,
  );
  const generalSignupTeaserEnabled = pathOr(
    false,
    ['general_signup_teasor_enabled'],
    capabilities,
  );
  const generalGetStartedProductTitle = pathOr(
    '',
    ['general_getstarted_product_title'],
    capabilities,
  );
  const generalFDICEnabled = pathOr(
    false,
    ['general_fdic_logo_enabled'],
    capabilities,
  );
  const generalHousingLogoEnabled = pathOr(
    false,
    ['general_equalhousing_logo_enabled'],
    capabilities,
  );
  const generalNMLS = pathOr('', ['general_nmls'], capabilities);
  const generalTermsPre = pathOr('', ['general_terms_pre'], capabilities);
  const generalPolicyPre = pathOr('', ['general_policy_pre'], capabilities);
  const generalPolicyLink = pathOr('', ['general_policy_link'], capabilities);
  const generalGetstartedWithTilesEnabled = pathOr(
    false,
    ['general_getstarted_withtiles_enabled'],
    capabilities,
  );
  const generalPasswordExpiryDays = pathOr(
    '',
    ['general_password_expiry_days'],
    capabilities,
  );
  const generalPasswordExpiryRemainderDays = pathOr(
    '',
    ['general_password_expiry_remainder_days'],
    capabilities,
  );
  const generalPasswordExpirySkipRoles = pathOr(
    '',
    ['general_password_expiry_skip_roles'],
    capabilities,
  );

  const generalRatesEnabled = equals(
    pathOr('', ['general_rates_enabled'], capabilities),
    TRUE,
  );

  const generalGetStartedLoanType = pathOr(
    '',
    ['general_getstarted_loan_type'],
    capabilities,
  );
  const generalTilesGroup = pathOr('', ['general_tiles_group'], capabilities);
  const generalWorkletApi = pathOr(
    'https://workletdev.lendsmart.ai/api/v1',
    ['general_worklet_api'],
    capabilities,
  );
  const generalDummyQuotes = pathOr('', ['general_dummy_quotes'], capabilities);
  const generalTimezoneDuration = pathOr(
    '',
    ['general_timezone_duration'],
    capabilities,
  );
  const generalSessionTimeOutForApplicant = pathOr(
    '',
    ['general_session_timeout_for_applicant'],
    capabilities,
  );
  const generalDisableMarketingAlerts = pathOr(
    FALSE,
    ['general_disable_marketing_alerts'],
    capabilities,
  );
  const generalPropertyAppraiser = pathOr(
    'housecanary',
    ['general_property_appraiser'],
    capabilities,
  );
  const generalMarketplaceLink = pathOr(
    '',
    ['general_marketplace_link_for_applicant'],
    capabilities,
  );
  const generalEnableBranch = pathOr(
    FALSE,
    ['general_enable_branch'],
    capabilities,
  );

  const generalMoxoDomain = pathOr('', ['general_moxo_domain'], capabilities);

  const generalMoxoChannel = pathOr(1, ['general_moxo_channel'], capabilities);

  /*** 2. storage: related flags */
  const storageAwsRegion = pathOr('', ['storage_aws_region'], capabilities);

  /*** 3. Engg: related flags */
  const lenderContactEmail = pathOr(
    'noreply@lendsmart.ai',
    ['lender_contact_email'],
    capabilities,
  );
  const lenderContactPhone = pathOr(
    '419-426-3641',
    ['lender_contact_phone'],
    capabilities,
  );
  const lenderContactAddress = pathOr(
    '111E Washington St.',
    ['lender_contact_address'],
    capabilities,
  );
  const lenderContactWeeklyTime = pathOr(
    '',
    ['lender_weekly_time'],
    capabilities,
  );
  const lenderContactWeekendTime = pathOr(
    '',
    ['lender_weekend_time'],
    capabilities,
  );
  const lenderBranchAddress = pathOr(
    '',
    ['lender_branch_contact_address'],
    capabilities,
  );
  const lenderBranchTitle = pathOr('', ['lender_branch_title'], capabilities);
  const supportSubmitNotificationEnabled = pathOr(
    false,
    ['support_submit_notification_enabled'],
    capabilities,
  );
  const supportReachoutEmail = pathOr(
    'demofundingsp@lendsmart.ai',
    ['support_reachout_email'],
    capabilities,
  );

  /*** 4. Dashboard: related flags */
  const dashboardChatPanelActive = pathOr(
    '#6A2CD8, #23CE6B, #F9CB40, #F2621B',
    ['dashboard_chat_panel_active'],
    capabilities,
  );
  const dashboardChatPanelDeActive = pathOr(
    '#6A2CD8, #23CE6B, #F9CB40, #F2621B',
    ['dashboard_chat_panel_deactive'],
    capabilities,
  );
  const dashboardClosingCost = pathOr(
    '#6A2CD8, #23CE6B, #F9CB40',
    ['dashboard_closing_cost'],
    capabilities,
  );
  const dashboardErrorClosingCost = pathOr(
    '#F0EAFB, #F0EAFB, #F0EAFB',
    ['dashboard_error_closing_cost'],
    capabilities,
  );
  const dashboardDefaultInterestRate = pathOr(
    '5',
    ['dashboard_default_interest_rate'],
    capabilities,
  );
  const dashboardMaximumInterestRate = pathOr(
    '8',
    ['dashboard_maximum_interest_rate'],
    capabilities,
  );
  const dashboardDefaultHomeInsurance = pathOr(
    125,
    ['dashboard_default_home_insurance'],
    capabilities,
  );
  const dashboardDisableLoanClosed = pathOr(
    FALSE,
    ['dashboard_disable_loan_closed'],
    capabilities,
  );

  /*** 5. Automacth: related flags */
  const automatchBrokerEmail = pathOr(
    'iso1@lendsmart.ai',
    ['automatch_broker_email'],
    capabilities,
  );
  const automatchRealtorEnabled = pathOr(
    '',
    ['automatch_realtor_enabled'],
    capabilities,
  );
  const automatchBankPreferredCity = pathOr(
    '',
    ['automatch_bank_preferred_lo_city'],
    capabilities,
  );

  /*** 6. Report: related flags */
  const reportMetabaseSiteEndpoint = pathOr(
    '',
    ['report_metabase_site_endpoint'],
    capabilities,
  );
  const reportMortgageKey = pathOr(
    "139",
    ['report_mortgage_key'],
    capabilities,
  );
  const reportGeneralCardKey = pathOr(
    '',
    ['report_general_card_key'],
    capabilities,
  );

  const reportGeneralHelocKey = pathOr(
    "502",
    ['report_general_heloc_key'],
    capabilities,
  );

  const reportMarketingKey = pathOr(
    "148",
    ['report_marketing_key'],
    capabilities,
  );
  const reportProductionStatisticsKey = pathOr(
    "152",
    ['report_productionstatistics_key'],
    capabilities,
  );
  const reportUwKey = pathOr("1", ['report_uw_key'], capabilities);
  const reportOfferKey = pathOr("34", ['report_offer_key'], capabilities);
  const reportPaymentHoldKey = pathOr(
    "70",
    ['report_payment_hold_key'],
    capabilities,
  );
  const reportPaymentDeliquentKey = pathOr(
    "71",
    ['report_payment_deliquent_key'],
    capabilities,
  );
  const reportOnboardServicingKey = pathOr(
    "140",
    ['report_onboard_servicing_key'],
    capabilities,
  );
  const reportBrokerYtdSubmissionKey = pathOr(
    "105",
    ['report_broker_ytd_submission_key'],
    capabilities,
  );
  const reportDeclinedAppsKey = pathOr(
    "155",
    ['report_declined_apps_key'],
    capabilities,
  );
  const reportDummyNamespace = pathOr(
    '',
    ['report_dummy_namespace'],
    capabilities,
  );
  const reportUserActivityKey = pathOr(
    "174",
    ['report_user_activity_key'],
    capabilities,
  );

  const reportGeneralFullKey = pathOr(
    "435",
    ['report_general_full_key'],
    capabilities,
  );

  const reportDenialLogSheetKey = pathOr(
    "436",
    ['report_denial_log_sheet_key'],
    capabilities,
  );

  const reportLowOwlQuarterlyDenialsKey = pathOr(
    "437",
    ['report_low_owl_quarterly_denials_key'],
    capabilities,
  );
  const reportMLACoveredBorrowerKey = pathOr(
    "438",
    ['report_mla_covered_borrower_key'],
    capabilities,
  );

  const reportInternalUsersKey = pathOr(
    '',
    ['report_internal_users_key'],
    capabilities,
  );

  const shouldEnableCustomReportConfig = equals(pathOr(
    'false',
    ['should_enable_report_custom_config'],
    capabilities
  ), 'true')

  /*** 7. Pipeline: related flags */
  const pipelineLoanProducts = pathOr(
    '',
    ['pipeline_loan_products'],
    capabilities,
  );
  const pipelineUseInitialStatus = pathOr(
    'Application Pending/Not Submitted',
    ['pipeline_use_initial_status'],
    capabilities,
  );
  const pipeLineNotMatchForAllApps = pathOr(
    '',
    ['pipeline_notmatch_for_all_apps'],
    capabilities,
  );
  const pipeLineNotMatchForMyApps = pathOr(
    '',
    ['pipeline_notmatch_for_my_apps'],
    capabilities,
  );
  const pipeLineNameForAllApps = pathOr(
    'All Applications',
    ['pipeline_name_for_all_apps'],
    capabilities,
  );
  const pipeLineMatchForMyApps = pathOr(
    '',
    ['pipeline_match_for_my_apps'],
    capabilities,
  );

  const pipelineMatchingForPending = pathOr(
    '',
    ['pipeline_match_for_pending'],
    capabilities,
  );
  const pipeLineSkipStatusUpateOnSubmit = pathOr(
    '',
    ['pipeline_skip_status_update_on_submit'],
    capabilities,
  );
  const pipeLineUseSubmittedStatus = pathOr(
    'Submitted',
    ['pipeline_use_submitted_status'],
    capabilities,
  );
  const pipeLineAssignWithAddTeamEnabled = pathOr(
    '',
    ['pipeline_assign_with_team_enabled'],
    capabilities,
  );

  const pipeLineClosedStages = pathOr(
    'Declined|Withdrawn|Loan Closed',
    ['pipeline_closed_stages'],
    capabilities,
  );

  /*** 8. UW: related flags */
  const uwInstantOfferThreshold = pathOr(
    25000,
    ['uw_instant_offer_threshold'],
    capabilities,
  );
  const uwMinOfferThreshold = pathOr(
    500,
    ['uw_minoffer_threshold'],
    capabilities,
  );
  const uwReApplyMessage = pathOr('', ['uw_reapply_message'], capabilities);
  const uwReApplyRule = pathOr('', ['uw_reapply_rule'], capabilities);
  const uwReApplyDays = pathOr(
    0,
    ['uw_wait_and_reapply_in_days'],
    capabilities,
  );
  const uwAcceptState = pathOr('', ['uw_accept_state'], capabilities);
  const uwProductTypes = pathOr([], ['uw_product_types'], capabilities);
  const uwDataMerchEnabled = pathOr([], ['uw_datamerch_enabled'], capabilities);

  /*** 9. Document: related flags */
  const documentUseBankStatementCategory = pathOr(
    '',
    ['document_use_bank_statement_category'],
    capabilities,
  );
  const documentUseBankStatementArtifact = pathOr(
    '',
    ['document_use_bank_statement_artifact'],
    capabilities,
  );

  /*** 10. Plugin: related flags */
  const plunginOcrolusEnabled = pathOr(
    false,
    ['plugin_ocrolus_enabled'],
    capabilities,
  );
  const plunginAuth0Enabled = pathOr(
    false,
    ['plugin_auth0_enabled'],
    capabilities,
  );

  /*** 11. Task: related flags */
  const topicTaskColumnDescription = pathOr(
    'file',
    ['topic_task_column_description'],
    capabilities,
  );

  /*** 12. Docusign: related flags */
  const docuSignContractName = pathOr(
    '',
    ['docusign_contract_name'],
    capabilities,
  );

  /*** 13. Notify: related flags */
  const notifySignUpEmailEnabled = pathOr(
    false,
    ['notify_signup_email_enabled'],
    capabilities,
  );
  const notifierSenderEmail = pathOr(
    'accounts@lendsmart.ai',
    ['notify_sender_email'],
    capabilities,
  );

  /*** 14. OneLoan: related flags */
  const oneloanRunFnPDFApplication = pathOr(
    '',
    ['oneloan_runfn_pdfapplication'],
    capabilities,
  );
  const oneloanRunFnExportXMLEnvelop = pathOr(
    'freddiemac',
    ['oneloan_runfn_export_xml_envelop'],
    capabilities,
  );
  const oneloanVisitApplication = pathOr(
    FALSE,
    ['oneloan_visit_application'],
    capabilities,
  );
  const oneloanEnableRunActionDeclined = pathOr(
    FALSE,
    ['oneloan_enable_runaction_for_declined'],
    capabilities,
  );

  /*** 15. Permission: related flags */
  const associateOwnerPermissionEnabled = pathOr(
    '',
    ['permission_associate_owner_enabled'],
    capabilities,
  );
  const associateOwnerRole = pathOr(
    'adminiso',
    ['permission_associate_owner_role'],
    capabilities,
  );
  const getCoreAPIAuthProvider = pathOr(
    DEFAULT_CORE_API_AUTH,
    ['general_authentication_provider'],
    capabilities,
  );
  const graphqlAuthenticationFacade = pathOr(
    '',
    ['general_graphql_authentication_facade'],
    capabilities,
  );

  const hideStageLabelOnDashboard = pathOr(
    FALSE,
    ['dashboard_hide_stage_label'],
    capabilities,
  );

  const generalHideGoogleSign = pathOr(
    FALSE,
    ['general_hide_google_signup'],
    capabilities,
  );

  /**
   * This config have to move to document based config for each tenant
   * supported values are mandatory/optional/none
   */
  const generalPlaidDocumentIdConnect = pathOr(
    'none',
    ['general_plaid_document_id_connect'],
    capabilities,
  );

  const generalPlaidEnableOFAC = pathOr(
    'false',
    ['general_plaid_enable_ofac'],
    capabilities,
  );


  const genralMultipleTaskTypeEnable = pathOr(
    FALSE,
    ['general_multiple_task_type_enable'],
    capabilities,
  );

  const canCoApplicantWithoutEmail = equals(
    TRUE,
    pathOr(
      FALSE,
      ['oneloan_allow_invite_coapplicant_without_email'],
      capabilities,
    ),
  );

  const canApplyWithoutPrimaryEmail = equals(
    TRUE,
    pathOr(
      FALSE,
      ['general_allow_internal_applications_without_primary_email'],
      capabilities,
    ),
  );

  /**
  * This is used to indicate tapabilities
    )hat we are going to use direct s3 connect or core_api signature URL connect
  * supported values are directaws or coreapi
  */
  const generalAWSS3Connect = pathOr(
    'coreapi',
    ['general_aws_s3_connect'],
    capabilities,
  );

  const generalAWSS3ConnectRetryCount = pathOr(
    '2',
    ['general_aws_s3_connect_retry_count'],
    capabilities,
  );

  const generalAWSS3LegacyBucket = pathOr(
    'attune-legacy-entity-assets-development',
    ['general_aws_s3_legacy_bucket'],
    capabilities,
  );

  const generalInActiveLogoutDisabled = pathOr(
    false,
    ['general_inactive_logout_disabled'],
    capabilities,
  );

  const generalInActiveLogoutTime = pathOr(
    '60',
    ['general_inactive_logout_time'],
    capabilities,
  );

  const generalValidationOnMarkAsComplete = pathOr(
    '',
    ['general_validate_on_mark_as_complete'],
    capabilities,
  );

  const documentDirectFileDownloadsFor = pathOr(
    '',
    ['document_direct_file_downloads_for'],
    capabilities,
  );

  // INFO: Notification drawer for bell
  const generalInAppNotification = pathOr(
    '',
    ['general_inapp_notification'],
    capabilities,
  );

  const generalAmazonS3BucketName = pathOr(
    '',
    ['general_aws_s3_bucket_name'],
    capabilities,
  );

  const oneloanLockMultipleUserAccessDisabled = equals(
    'true',
    pathOr('false', ['oneloan_lock_multiple_user_access_disable'],
      capabilities));

  const isMagicLinkEnabled = pipe(
    pathSatisfies((x: string) => Boolean(x), ['is_magic_link_enabled']),
    defaultTo(false)
  )(capabilities);

  const generalBankRoutingNumber = pathOr('', ['general_bank_routing_number'], capabilities);

  return {
    'uid': uid,
    'entity-id': entityID,
    'theme-logo-website-logo': themeWebsiteLogo,
    'theme-logo-website-logo-height': themeWebsiteLogoHeight,
    'theme-logo-website-logo-width': themeWebsiteLogoWidth,
    'theme-logo-website-welcome-page-logo': themeWebsiteWelcomePageLogo,
    'main-color': mainColor,
    'extra-light-color': extraLightColor,
    'primary-dark-color': primaryDarkColor,
    'text-color': textColor,
    'button-color': buttonColor,
    'button-border-active-color': buttonBorderActiveColor,
    'button-border-color': buttonBorderColor,
    'secodary-color': secondaryColor,
    'namespace-id': namespaceId,
    'graphql-app-endpoint': graphqlAppEndpoint,
    'graphql-serv-endpoint': graphqlServEndpoint,
    'general-app-title': generalAppTitle,
    'general-app-title-without-postfix': generalAppTitleWithoutPostFix,
    'general-home-root': generalHomeRoot,
    'general-default-root': generalDefaultRoot,
    'general-hero-page-enabled': generalHeroPageEnabled,
    'general-getstarted-loan-type': generalGetStartedLoanType,
    'general-smartlook-enabled': generalSmartlookEnabled,
    'general-web-session-consents-enabled': generalCookiePolicyConsent,
    'general-smartlook-trace-enabled': generalSmartlookTraceEnabled,
    'general-signup-with-title-enabled': generalSignupWithTitleEnabled,
    'general-signup-teaser-enabled': generalSignupTeaserEnabled,
    'general-getstarted-product-title': generalGetStartedProductTitle,
    'general-fdic-enabled': generalFDICEnabled,
    'general-housing-logo-enabled': generalHousingLogoEnabled,
    'general-nmls': generalNMLS,
    'general-terms-pre': generalTermsPre,
    'general-policy-pre': generalPolicyPre,
    'general-policy-link': generalPolicyLink,
    'general-getstarted-with-tile-enabled': generalGetstartedWithTilesEnabled,
    'general-rates-enabled': generalRatesEnabled,
    'general-tile-group': generalTilesGroup,
    'general-worklet-api': generalWorkletApi,
    'general-dummy-quotes': generalDummyQuotes,
    'general-timezone-duration': generalTimezoneDuration,
    'general-password-expiry-days': generalPasswordExpiryDays,
    'general-password-expiry-remainder-days':
      generalPasswordExpiryRemainderDays,
    'general-password-expiry-skip-roles': generalPasswordExpirySkipRoles,
    'general-multiple-task-type-enable': genralMultipleTaskTypeEnable,
    'storage-aws-region': storageAwsRegion,
    'lender-contact-email': lenderContactEmail,
    'lender-contact-phone': lenderContactPhone,
    'lender-contact-address': lenderContactAddress,
    'lender-weekly-time': lenderContactWeeklyTime,
    'lender-weekend-time': lenderContactWeekendTime,
    'lender-branch-address': lenderBranchAddress,
    'lender-branch-title': lenderBranchTitle,
    'support-submit-notification-enabled': supportSubmitNotificationEnabled,
    'support-reachout-email': supportReachoutEmail,
    'dashboard-chat-panel-active': dashboardChatPanelActive,
    'dashboard-chat-panel-deactive': dashboardChatPanelDeActive,
    'dashboard-closing-cost': dashboardClosingCost,
    'dashboard-error-closing-cost': dashboardErrorClosingCost,
    'dashboard-default-interest-rate': dashboardDefaultInterestRate,
    'dashboard-maximum-interest-rate': dashboardMaximumInterestRate,
    'dashboard-default-home-insurance': dashboardDefaultHomeInsurance,
    'dashboard-disable-loan-closed': dashboardDisableLoanClosed,
    'oneloan-visit-application': oneloanVisitApplication,
    'oneloan-enable-runaction-declined': oneloanEnableRunActionDeclined,
    'automatch-broker-email': automatchBrokerEmail,
    'automatch-realtor-enabled': automatchRealtorEnabled,
    'automatch-bank-preferred-city': automatchBankPreferredCity,
    'report-metabase-site-endpoint': reportMetabaseSiteEndpoint,
    'report-mortgage-key': reportMortgageKey,
    'report-general-card-key': reportGeneralCardKey,
    'report-general-heloc-key': reportGeneralHelocKey,
    'report-marketing-key': reportMarketingKey,
    'report-productionstatistics-key': reportProductionStatisticsKey,
    'report-uw-key': reportUwKey,
    'report-offer-key': reportOfferKey,
    'report-payment-hold-key': reportPaymentHoldKey,
    'report-payment-deliquent-key': reportPaymentDeliquentKey,
    'report-onboard-servicing-key': reportOnboardServicingKey,
    'report-broker-ytd-submission-key': reportBrokerYtdSubmissionKey,
    'report-declined-apps-key': reportDeclinedAppsKey,
    'report-user-activity-key': reportUserActivityKey,
    'report-general-full-key': reportGeneralFullKey,
    'report-denial-log-sheet-key': reportDenialLogSheetKey,
    'report-low-owl-quarterly-denials-key': reportLowOwlQuarterlyDenialsKey,
    'report-mla-covered-borrower-key': reportMLACoveredBorrowerKey,
    'report-internal-users-key': reportInternalUsersKey,
    'report-dummy-namespace': reportDummyNamespace,
    'pipeline-loan-products': pipelineLoanProducts,
    'pipeline-use-initial-status': pipelineUseInitialStatus,
    'pipeline-not-match-for-all-apps': pipeLineNotMatchForAllApps,
    'pipeline-not-match-for-my-apps': pipeLineNotMatchForMyApps,
    'pipeline-name-for-all-apps': pipeLineNameForAllApps,
    'pipeline-match-for-pending': pipelineMatchingForPending,
    'pipeline-match-for-my-apps': pipeLineMatchForMyApps,
    'pipeline-skip-status-update-on-submit': pipeLineSkipStatusUpateOnSubmit,
    'pipeline-use-submitted-status': pipeLineUseSubmittedStatus,
    'pipeline-closed-stages': pipeLineClosedStages,
    'pipeline-assign-with-add-team-enabled': pipeLineAssignWithAddTeamEnabled,
    'uw-instant-offer-threshold': uwInstantOfferThreshold,
    'uw-min-offer-threshold': uwMinOfferThreshold,
    'uw-reapply-message': uwReApplyMessage,
    'uw-reapply-rule': uwReApplyRule,
    'uw-reapply-days': uwReApplyDays,
    'uw-accept-state': uwAcceptState,
    'uw-product-types': uwProductTypes,
    'uw-datamerch-enabled': uwDataMerchEnabled,
    'document-use-bankstatement-category': documentUseBankStatementCategory,
    'document-use-bankstatement-artifact': documentUseBankStatementArtifact,
    'plungin-ocrolus-enabled': plunginOcrolusEnabled,
    'plungin-auth0-enabled': plunginAuth0Enabled,
    'topic-task-column-description': topicTaskColumnDescription,
    'docusign-contract-name': docuSignContractName,
    'notify-signup-email-enabled': notifySignUpEmailEnabled,
    'notifier-sender-email': notifierSenderEmail,
    'one-loan-runfn-pdf-application': oneloanRunFnPDFApplication,
    'one-loan-export-xml-envelop': oneloanRunFnExportXMLEnvelop,
    'associate-owner-permission-enabled': associateOwnerPermissionEnabled,
    'associate-owner-role': associateOwnerRole,
    'general-authentication-provider': getCoreAPIAuthProvider,
    'general-graphql-authentication-facade': graphqlAuthenticationFacade,
    'general-plaid-document-id-connect': generalPlaidDocumentIdConnect,
    'general-plaid-enable-ofca': generalPlaidEnableOFAC,
    'general-session-timout-for-applicant': generalSessionTimeOutForApplicant,
    'general-disable-marketing-alerts': generalDisableMarketingAlerts,
    'general-property-appraiser': generalPropertyAppraiser,
    'general-marketplace-link': generalMarketplaceLink,
    'general-aws-s3-connect': generalAWSS3Connect,
    'general-aws-s3-connect-retry-count': generalAWSS3ConnectRetryCount,
    'general-aws-s3-legacy-bucket': generalAWSS3LegacyBucket,

    'general-enable-branch': generalEnableBranch,
    'general-inactive-logout-disabled': generalInActiveLogoutDisabled,
    'general-inactive-logout-time': generalInActiveLogoutTime,
    'document-direct-file-downloands-for': documentDirectFileDownloadsFor,
    'general-inapp-notification': generalInAppNotification,
    'who-can-access-interal-settings-emails':
      whoCanAccessInternalGlobalSettingsEmails,
    'general-aws-s3-bucket-name': generalAmazonS3BucketName,
    'general-validate-mark-as-complete': generalValidationOnMarkAsComplete,
    'dashboard-hide-stage-label': hideStageLabelOnDashboard,
    'general-hide-google-signup': generalHideGoogleSign,
    'oneloan-allow-coapplicant-without-email': canCoApplicantWithoutEmail,
    'general-applications-without-primary-email': canApplyWithoutPrimaryEmail,
    'general-moxo-domain': generalMoxoDomain,
    'general-moxo-channel': generalMoxoChannel,
    'oneloan-lock-multiple-user-access-disable': oneloanLockMultipleUserAccessDisabled,
    'is-magic-link-enabled': isMagicLinkEnabled,
    'should-enable-custom-report-config': shouldEnableCustomReportConfig,
    'general_site_cookie_policy_link': generalSiteCookiePolicyLink,
    'general_bank_routing_number': generalBankRoutingNumber,
  };
};

/**
 * convertSiteSettingsFrom is used to make site settings into function map *
 * NOTE: exported to overide upon unit test
 */

export const convertSiteSettingsFrom = (siteSetting: any) => {
  if (canUseLocalDevelopmentConfig) {
    return convertSiteSettingsFlags(pathOr(siteSetting, ['data'], siteSetting));
  }
  return convertSiteSettingsFlags(siteSetting);
};

export type SiteSettingList =
  | 'uid'
  | 'entity-id'
  | 'theme-logo-website-logo'
  | 'theme-logo-website-logo-height'
  | 'theme-logo-website-logo-width'
  | 'theme-logo-website-welcome-page-logo'
  | 'main-color'
  | 'extra-light-color'
  | 'primary-dark-color'
  | 'text-color'
  | 'button-color'
  | 'button-border-active-color'
  | 'button-border-color'
  | 'secodary-color'
  | 'namespace-id'
  | 'graphql-app-endpoint'
  | 'graphql-serv-endpoint'
  | 'general-app-title'
  | 'general-app-title-without-postfix'
  | 'general-home-root'
  | 'general-default-root'
  | 'general-hero-page-enabled'
  | 'general-getstarted-loan-type'
  | 'general-smartlook-enabled'
  | 'general-smartlook-trace-enabled'
  | 'general-signup-with-title-enabled'
  | 'general-signup-teaser-enabled'
  | 'general-getstarted-product-title'
  | 'general-fdic-enabled'
  | 'general-housing-logo-enabled'
  | 'general-terms-pre'
  | 'general-policy-pre'
  | 'general-policy-link'
  | 'general-getstarted-with-tile-enabled'
  | 'general-rates-enabled'
  | 'general-tile-group'
  | 'general-worklet-api'
  | 'general-dummy-quotes'
  | 'general-timezone-duration'
  | 'general-password-expiry-days'
  | 'general-password-expiry-remainder-days'
  | 'general-password-expiry-skip-roles'
  | 'general-plaid-enable-ofca'
  | 'storage-aws-region'
  | 'support-non-us-phone'
  | 'lender-contact-email'
  | 'lender-contact-phone'
  | 'lender-contact-address'
  | 'lender-weekly-time'
  | 'lender-weekend-time'
  | 'lender-branch-address'
  | 'lender-branch-title'
  | 'support-submit-notification-enabled'
  | 'support-reachout-email'
  | 'dashboard-chat-panel-active'
  | 'dashboard-chat-panel-deactive'
  | 'dashboard-closing-cost'
  | 'dashboard-error-closing-cost'
  | 'dashboard-default-interest-rate'
  | 'dashboard-maximum-interest-rate'
  | 'dashboard-default-home-insurance'
  | 'dashboard-disable-loan-closed'
  | 'oneloan-visit-application'
  | 'oneloan-enable-runaction-declined'
  | 'automatch-broker-email'
  | 'automatch-realtor-enabled'
  | 'automatch-bank-preferred-city'
  | 'report-metabase-site-endpoint'
  | 'report-mortgage-key'
  | 'report-general-card-key'
  | 'report-general-heloc-key'
  | 'report-productionstatistics-key'
  | 'report-marketing-key'
  | 'report-uw-key'
  | 'report-offer-key'
  | 'report-payment-hold-key'
  | 'report-payment-deliquent-key'
  | 'report-onboard-servicing-key'
  | 'report-broker-ytd-submission-key'
  | 'report-declined-apps-key'
  | 'report-user-activity-key'
  | 'report-general-full-key'
  | 'report-denial-log-sheet-key'
  | 'report-low-owl-quarterly-denials-key'
  | 'report-mla-covered-borrower-key'
  | 'report-internal-users-key'
  | 'report-dummy-namespace'
  | 'pipeline-loan-products'
  | 'pipeline-use-initial-status'
  | 'pipeline-not-match-for-all-apps'
  | 'pipeline-not-match-for-my-apps'
  | 'pipeline-name-for-all-apps'
  | 'pipeline-name-for-my-apps'
  | 'pipeline-match-for-my-apps'
  | 'pipeline-skip-status-update-on-submit'
  | 'pipeline-submit-when-ready'
  | 'pipeline-use-submitted-status'
  | 'pipeline-assign-with-add-team-enabled'
  | 'pipeline-closed-stages'
  | 'uw-instant-offer-threshold'
  | 'uw-min-offer-threshold'
  | 'uw-reapply-message'
  | 'uw-reapply-rule'
  | 'uw-reapply-days'
  | 'uw-accept-state'
  | 'uw-product-types'
  | 'uw-datamerch-enabled'
  | 'document-use-bankstatement-category'
  | 'document-use-bankstatement-artifact'
  | 'plungin-ocrolus-enabled'
  | 'plungin-auth0-enabled'
  | 'topic-task-column-description'
  | 'docusign-contract-name'
  | 'notify-signup-email-enabled'
  | 'notifier-sender-email'
  | 'one-loan-runfn-pdf-application'
  | 'one-loan-export-xml-envelop'
  | 'associate-owner-permission-enabled'
  | 'associate-owner-role'
  | 'general-authentication-provider'
  | 'general-graphql-authentication-facade'
  | 'general-session-timout-for-applicant'
  | 'general-disable-marketing-alerts'
  | 'general-property-appraiser'
  | 'general-marketplace-link'
  | 'general-nmls'
  | 'general-plaid-document-id-connect'
  | 'general-aws-s3-connect'
  | 'general-aws-s3-connect-retry-count'
  | 'general-enable-branch'
  | 'general-inactive-logout-disabled'
  | 'general-inactive-logout-time'
  | 'general-multiple-task-type-enable'
  | 'document-direct-file-downloands-for'
  | 'general-inapp-notification'
  | 'who-can-access-interal-settings-emails'
  | 'general-aws-s3-bucket-name'
  | 'general-validate-mark-as-complete'
  | 'dashboard-hide-stage-label'
  | 'general-hide-google-signup'
  | 'pipeline-match-for-pending'
  | 'oneloan-allow-coapplicant-without-email'
  | 'general-applications-without-primary-email'
  | 'general-aws-s3-legacy-bucket'
  | 'general-moxo-domain'
  | 'general-moxo-channel'
  | 'oneloan-lock-multiple-user-access-disable'
  | 'is-magic-link-enabled'
  | 'general-web-session-consents-enabled'
  | 'should-enable-custom-report-config'
  | 'general_site_cookie_policy_link'
  | 'general_bank_routing_number'

type SiteSettingsMap = Record<SiteSettingList, any>;

type SiteSettingListener = (value: any) => void;

class SiteSettings {
  _sitesettings: Partial<SiteSettingsMap>;
  _listeners: Partial<Record<SiteSettingList, SiteSettingListener[]>> = {};

  constructor(SiteSettings: Partial<SiteSettingsMap> = {}) {
    this._sitesettings = SiteSettings;
  }
  get(key: SiteSettingList, defaultValue: any = '') {
    return this._sitesettings[key] !== undefined
      ? this._sitesettings[key]
      : defaultValue;
  }

  getAll() {
    return this._sitesettings;
  }
  set(key: SiteSettingList, value: any) {
    if (this._sitesettings[key] !== value) {
      this._sitesettings[key] = value;
      const listeners = this._listeners[key];

      if (!listeners) {
        return;
      }

      for (const listener of listeners) {
        setTimeout(() => listener(value));
      }
    }
  }
  setAll(settings: SiteSettingsMap) {
    const keys = Object.keys(settings) as SiteSettingList[];

    keys.forEach((key) => {
      this.set(key, settings[key]);
    });
  }

  getUID() {
    return this.get('uid');
  }
  getEntityID() {
    return this.get('entity-id');
  }

  themeWebsiteLogo() {
    return this.get('theme-logo-website-logo');
  }
  themeWebsiteLogoHeight() {
    return this.get('theme-logo-website-logo-height');
  }

  themeWebsiteLogoWidth(){
    return this.get('theme-logo-website-logo-width', 'auto')
  }

  themeWebsiteWelcomePageLogo() {
    return this.get('theme-logo-website-welcome-page-logo');
  }

  mainColor() {
    return this.get('main-color');
  }
  extraLightColor() {
    return this.get('extra-light-color');
  }
  primaryDarkColor() {
    return this.get('primary-dark-color');
  }
  textColor() {
    return this.get('text-color');
  }
  buttonColor() {
    return this.get('button-color');
  }
  buttonBorderActiveColor() {
    return this.get('button-border-active-color');
  }
  buttonBorderColor() {
    return this.get('button-border-color');
  }
  secondaryColor() {
    return this.get('secodary-color');
  }
  namespaceId() {
    return this.get('namespace-id');
  }
  graphqlAppEndpoint() {
    return this.get('graphql-app-endpoint');
  }
  graphqlServEndpoint() {
    return this.get('graphql-serv-endpoint');
  }
  generalAppTitle() {
    return this.get('general-app-title');
  }
  generalAppTitleWithoutPostFix() {
    return this.get('general-app-title-without-postfix');
  }
  generalHomeRoot() {
    return this.get('general-home-root');
  }
  generalGetStartedLoanType() {
    return this.get('general-getstarted-loan-type');
  }
  generalSmartlookEnabled() {
    return this.get('general-smartlook-enabled');
  }

  generalCookiePolicyConsent() {
        return this.get('general-web-session-consents-enabled');
  }

  generalSmartlookTraceEnabled() {
    return this.get('general-smartlook-trace-enabled');
  }
  generalSignupWithTitleEnabled() {
    return this.get('general-signup-with-title-enabled');
  }
  generalSignupTeaserEnabled() {
    return this.get('general-signup-teaser-enabled');
  }
  generalGetStartedProductTitle() {
    return this.get('general-getstarted-product-title');
  }
  generalFDICEnabled() {
    return this.get('general-fdic-enabled');
  }
  generalHousingLogoEnabled() {
    return this.get('general-housing-logo-enabled');
  }
  generalNMLS() {
    return this.get('general-nmls');
  }
  generalTermsPre() {
    return this.get('general-terms-pre');
  }
  generalPolicyPre() {
    return this.get('general-policy-pre');
  }
  generalPolicyLink() {
    return this.get('general-policy-link');
  }
  generalGetstartedWithTilesEnabled() {
    return this.get('general-getstarted-with-tile-enabled');
  }
  generalRatesEnabled() {
    return this.get('general-rates-enabled');
  }
  generalGetstartedWithTilesGroup() {
    return this.get('general-tile-group');
  }

  generalWorkletApi() {
    return this.get('general-worklet-api');
  }
  generalDummyQuotes() {
    return this.get('general-dummy-quotes');
  }
  generalTimezoneDuration() {
    return this.get('general-timezone-duration');
  }

  generalPasswordExpiryDays() {
    return this.get('general-password-expiry-days');
  }
  generalPasswordRemainderDays() {
    return this.get('general-password-expiry-remainder-days');
  }
  generalPasswordExpirySkipRoles() {
    return this.get('general-password-expiry-skip-roles');
  }
  generalMultipleTaskTypeEnable() {
    return this.get('general-multiple-task-type-enable');
  }
  generalAWSS3LegacyBucket() {
    return this.get('general-aws-s3-legacy-bucket');
  }

  storageAwsRegion() {
    return this.get('storage-aws-region');
  }
  lenderContactEmail() {
    return this.get('lender-contact-email');
  }
  lenderContactPhone() {
    return this.get('lender-contact-phone');
  }
  lenderContactAddress() {
    return this.get('lender-contact-address');
  }
  lenderContactWeeklyTime() {
    return this.get('lender-weekly-time');
  }
  lenderContactWeekendTime() {
    return this.get('lender-weekend-time');
  }
  lenderBranchAddress() {
    return this.get('lender-branch-address');
  }
  lenderBranchTitle() {
    return this.get('lender-branch-title');
  }
  supportSubmitNotificationEnabled() {
    return this.get('support-submit-notification-enabled');
  }
  supportReachoutEmail() {
    return this.get('support-reachout-email');
  }

  dashboardChatPanelActive() {
    return this.get('dashboard-chat-panel-active');
  }
  dashboardChatPanelDeActive() {
    return this.get('dashboard-chat-panel-deactive');
  }
  dashboardClosingCost() {
    return this.get('dashboard-closing-cost');
  }
  dashboardErrorClosingCost() {
    return this.get('dashboard-error-closing-cost');
  }
  dashboardDefaultInterestRate() {
    return this.get('dashboard-default-interest-rate');
  }
  dashboardMaximumInterestRate() {
    return this.get('dashboard-maximum-interest-rate');
  }
  dashboardDefaultHomeInsurance() {
    return this.get('dashboard-default-home-insurance');
  }
  dashboardDisableLoanClosed() {
    return this.get('dashboard-disable-loan-closed');
  }
  oneloanVisitApplication() {
    return this.get('oneloan-visit-application');
  }
  oneloanEnableRunActionDeclined() {
    return this.get('oneloan-enable-runaction-declined');
  }

  automatchBrokerEmail() {
    return this.get('automatch-broker-email');
  }
  automatchRealtorEnabled() {
    return this.get('automatch-realtor-enabled');
  }
  automatchBankPreferredCity() {
    return this.get('automatch-bank-preferred-city');
  }
  reportMetabaseSiteEndpoint() {
    return this.get('report-metabase-site-endpoint');
  }
  reportMortgageKey() {
    return this.get('report-mortgage-key');
  }
  reportGeneralCardKey() {
    return this.get('report-general-card-key');
  }
  reportGeneralHelocKey() {
    return this.get('report-general-heloc-key');
  }
  reportMarketingKey() {
    return this.get('report-marketing-key');
  }
  reportProductionStatisticsKey() {
    return this.get('report-productionstatistics-key');
  }
  reportUwKey() {
    return this.get('report-uw-key');
  }
  reportOfferKey() {
    return this.get('report-offer-key');
  }
  reportPaymentHoldKey() {
    return this.get('report-payment-hold-key');
  }

  reportPaymentDeliquentKey() {
    return this.get('report-payment-deliquent-key');
  }
  reportOnboardServicingKey() {
    return this.get('report-onboard-servicing-key');
  }

  reportBrokerYtdSubmissionKey() {
    return this.get('report-broker-ytd-submission-key');
  }
  reportDeclinedAppsKey() {
    return this.get('report-declined-apps-key');
  }
  reportUserActivityKey() {
    return this.get('report-user-activity-key');
  }
  reportGeneralFullKey() {
    return this.get('report-general-full-key');
  }
  reportDenialLogSheetKey() {
    return this.get('report-denial-log-sheet-key');
  }
  reportLowOwlQuarterlyDenialsKey() {
    return this.get('report-low-owl-quarterly-denials-key');
  }
  reportMLACoveredBorrowerKey() {
    return this.get('report-mla-covered-borrower-key');
  }
  reportInternalUsersKey() {
    return this.get('report-internal-users-key');
  }

  reportDummyNamespace() {
    return this.get('report-dummy-namespace');
  }
  pipelineLoanProducts() {
    return this.get('pipeline-loan-products');
  }
  pipelineUseInitialStatus() {
    return this.get('pipeline-use-initial-status');
  }
  pipeLineNotMatchForAllApps() {
    return this.get('pipeline-not-match-for-all-apps');
  }
  pipeLineNotMatchForMyApps() {
    return this.get('pipeline-not-match-for-my-apps');
  }
  pipeLineNameForAllApps() {
    return this.get('pipeline-name-for-all-apps');
  }
  pipeLineMatchForMyApps() {
    return this.get('pipeline-match-for-my-apps');
  }
  pipeLineMatchForPending() {
    return this.get('pipeline-match-for-pending');
  }
  pipeLineSkipStatusUpateOnSubmit() {
    return this.get('pipeline-skip-status-update-on-submit');
  }

  pipeLineUseSubmittedStatus() {
    return this.get('pipeline-use-submitted-status');
  }

  pipeLineClosedStages() {
    return this.get('pipeline-closed-stages');
  }
  pipeLineAssignWithAddTeamEnabled() {
    return this.get('pipeline-assign-with-add-team-enabled');
  }
  uwInstantOfferThreshold() {
    return this.get('uw-instant-offer-threshold');
  }
  uwMinOfferThreshold() {
    return this.get('uw-min-offer-threshold');
  }
  uwReApplyMessage() {
    return this.get('uw-reapply-message');
  }
  uwReApplyRule() {
    return this.get('uw-reapply-rule');
  }
  uwReApplyDays() {
    return this.get('uw-reapply-days');
  }
  uwAcceptState() {
    return this.get('uw-accept-state');
  }
  uwProductTypes() {
    return this.get('uw-product-types');
  }
  uwDataMerchEnabled() {
    return this.get('uw-datamerch-enabled');
  }
  documentUseBankStatementCategory() {
    return this.get('document-use-bankstatement-category');
  }
  documentUseBankStatementArtifact() {
    return this.get('document-use-bankstatement-artifact');
  }
  plunginOcrolusEnabled() {
    return this.get('plungin-ocrolus-enabled');
  }
  plunginAuth0Enabled() {
    return this.get('plungin-auth0-enabled');
  }

  topicTaskColumnDescription() {
    return this.get('topic-task-column-description');
  }
  docuSignContractName() {
    return this.get('docusign-contract-name');
  }
  notifySignUpEmailEnabled() {
    return this.get('notify-signup-email-enabled');
  }
  notifierSenderEmail() {
    return this.get('notifier-sender-email');
  }
  oneloanRunFnPDFApplication() {
    return this.get('one-loan-runfn-pdf-application');
  }

  oneloanRunFnExportXMLEnvelop() {
    return this.get('one-loan-export-xml-envelop');
  }
  associateOwnerPermissionEnabled() {
    return this.get('associate-owner-permission-enabled');
  }
  associateOwnerRole() {
    return this.get('associate-owner-role');
  }
  getCoreAPIAuthProvider() {
    return this.get('general-authentication-provider');
  }

  graphqlAuthenticationFacade() {
    return this.get('general-graphql-authentication-facade');
  }

  generalSessionTimeOutForApplicant() {
    return this.get('general-session-timout-for-applicant');
  }

  generalDisableMarketingAlerts() {
    return this.get('general-disable-marketing-alerts');
  }

  generalPropertyAppraiser() {
    return this.get('general-property-appraiser');
  }
  generalEnableBranch() {
    return this.get('general-enable-branch');
  }

  generalMarketplaceLink() {
    return this.get('general-marketplace-link');
  }

  generalPlaidDocumentIdConnect() {
    return this.get('general-plaid-document-id-connect');
  }
  generalPlaidEnableOFAC() {
    return this.get('general-plaid-enable-ofca');
  }
  generalAWSS3Connect() {
    return this.get('general-aws-s3-connect');
  }

  generalAWSS3ConnectRetryCount() {
    return this.get('general-aws-s3-connect-retry-count');
  }

  generalInActiveLogoutDisabled() {
    return this.get('general-inactive-logout-disabled');
  }

  generalInActiveLogoutTime() {
    return this.get('general-inactive-logout-time');
  }

  generalInAppNotification() {
    return this.get('general-inapp-notification');
  }

  documentDirectFileDownloadsFor() {
    return this.get('document-direct-file-downloands-for');
  }

  whoCanAccessInternalGlobalSettingsEmails() {
    return this.get('who-can-access-interal-settings-emails');
  }

  generalAmazonS3BucketName() {
    return this.get('general-aws-s3-bucket-name');
  }

  generalDefaulRoot() {
    return this.get('general-default-root');
  }

  generalHeroPageEnabled() {
    return this.get('general-hero-page-enabled');
  }

  generalValidationOnMarkAsComplete() {
    return this.get('general-validate-mark-as-complete');
  }

  // can be formatted in two ways
  // true | false
  // RESIDENTIALMORTGAGE,PERSONALLOANS,loanTypeA
  // This now covers loanType disable or tenant wise disable
  shouldHideStageLabelOnDashboard() {
    return this.get('dashboard-hide-stage-label');
  }

  generalHideGoogleSignUp() {
    return this.get('general-hide-google-signup');
  }

  canCoApplicantWithoutEmail() {
    return this.get('oneloan-allow-coapplicant-without-email');
  }
  canApplyWithoutPrimaryEmail() {
    return this.get('general-applications-without-primary-email');
  }

  generalMoxoChannel() {
    return this.get('general-moxo-channel');
  }

  generalMoxoDomain() {
    return this.get('general-moxo-domain');
  }

  oneloanLockMultipleUserAccessDisabled() {
    return this.get('oneloan-lock-multiple-user-access-disable');
  }

  isMagicLinkEnabled(): boolean {
    return this.get('is-magic-link-enabled');
  }

  shouldEnableCustomReportConfig() {
    return this.get('should-enable-custom-report-config');
  }

  generalSiteCookiePolicyLink() {
    return this.get('general_site_cookie_policy_link');
  }

  generalBankRoutingNumber() {
    return this.get('general_bank_routing_number')
  }
}

export default new SiteSettings(
  //@ts-ignore
  convertSiteSettingsFrom((window as any).LendsmartBootstrap),
);
